import {ensurePreviousActiveDialogIsClosed} from '@github-ui/diff-lines/ensure-previous-active-dialog-is-closed'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CommentDiscussionIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useRef, useState} from 'react'

import {CommentsSidePanel} from './CommentsSidePanel'
import type {PullRequest} from '../../page-data/payloads/toolbar'
import type {PageLimits} from '../../page-data/payloads/files'
import {useIssueCommentsCount, useThreadCommentCount} from '../../stores/selectors'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export interface OpenCommentsSidePanelButtonProps {
  isFileTreeExpanded?: boolean
  pageLimits: PageLimits
  pullRequest: PullRequest
  tabSize?: number
}

/**
 * Renders a button that opens the comments side panel
 */
export function OpenCommentsSidePanelButton({
  isFileTreeExpanded,
  pageLimits,
  pullRequest,
  tabSize,
}: OpenCommentsSidePanelButtonProps) {
  const [commentsSidePanelIsOpen, setCommentsSidePanelIsOpen] = useState(false)
  const toggleSidePanelRef = useRef<HTMLButtonElement>(null)

  const diffCommentCount = useThreadCommentCount()
  const issueCommentsCount = useIssueCommentsCount()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const issueCommentsEnabled = isFeatureEnabled('comments_panel_issue_comments')

  const buttonCount = issueCommentsEnabled ? diffCommentCount + issueCommentsCount : diffCommentCount

  return (
    <div className="d-flex flex-items-center">
      <ErrorBoundary
        boundaryName="OpenCommentsSidePanelButton"
        fallback={
          <Button
            aria-label="The comments side panel cannot currently be opened."
            leadingVisual={CommentDiscussionIcon}
            size="small"
            variant="invisible"
          />
        }
      >
        <Button
          ref={toggleSidePanelRef}
          aria-label="Open comments side panel"
          count={buttonCount}
          leadingVisual={CommentDiscussionIcon}
          size="small"
          onClick={() => {
            sendPullRequestAnalyticsEvent('comments_side_panel.open', 'COMMENTS_SIDE_PANEL_BUTTON')
            ensurePreviousActiveDialogIsClosed()
            setCommentsSidePanelIsOpen(true)
          }}
        >
          {isFileTreeExpanded && <span className="d-none d-xl-block">Comments</span>}
        </Button>
        <CommentsSidePanel
          isOpen={commentsSidePanelIsOpen}
          pageLimits={pageLimits}
          pathName={pullRequest.pathName}
          pullRequestAuthorLogin={pullRequest.author.login}
          tabSize={tabSize}
          toggleSidesheetRef={toggleSidePanelRef}
          onClose={() => setCommentsSidePanelIsOpen(false)}
          diffCommentCount={diffCommentCount}
          issueCommentsCount={issueCommentsCount}
        />
      </ErrorBoundary>
    </div>
  )
}
